// Single source of truth for the net-worth asset-allocation donut.
// Used by the Consolidated Dashboard and the Portfolio Summary (Analytics)
// page so both always show the same buckets and percentages.

export const DONUT_COLORS = ['#ffd87a', '#e8a838', '#c97a3a', '#dae4c4', '#c8d9b0', '#a86830', '#eae9da', '#d9d9d9'];

export const PM_CATS = ['vc', 'pe', 'search', 'long_only', 'credit', 'infra', 'hedge_fund', 'secondaries', 'special_situations', 'pre_ipo', 'mezzanine', 'fof', 'co_investment', 'royalty', 'natural_resources'];
export const ALT_CATS = ['gold', 'art', 'cash', 'alternatives', 'ip'];

// Public-market asset class -> allocation bucket. Edit this table to regroup;
// classes not listed (incl. future ones) land in PUBLIC_OTHERS_BUCKET.
// 'fd' is intentionally absent: FDs are counted under Cash & Alternatives.
export const PUBLIC_BUCKETS = [
  { name: 'Stocks & MFs',        category: 'public_markets', color: '#ffd879', classes: ['stocks', 'mf', 'etf'] },
  { name: 'Debt',                category: 'fixed_income',   color: '#c97a3a', classes: ['bonds', 'ppf', 'nps'] },
  { name: 'Gold & Alternatives', category: 'alternatives',   color: '#d9e2c3', classes: ['gold', 'pms', 'aif', 'fo'] },
];
export const PUBLIC_OTHERS_BUCKET = { name: 'Others', category: 'public_markets_other', color: '#a8a8a8' };

// The API's asset_allocation is a % of the FULL public total (FDs included).
// Once FDs are carved out the base shrinks, so each remaining class must be
// re-based onto the FD-free total or the percentages sum to < 100 and the
// shortfall is mis-bucketed as "Others".
//   p'_c = p_c / (sum of p_k for k != fd) * 100
export const rebaseAllocationExcludingFD = (allocation = {}) => {
  const rest = Object.entries(allocation).filter(([cls, pct]) => cls !== 'fd' && pct > 0);
  const base = rest.reduce((s, [, pct]) => s + pct, 0);
  if (base <= 0) return {};
  return Object.fromEntries(rest.map(([cls, pct]) => [cls, (pct / base) * 100]));
};

const sum = (rows, fn) => (rows || []).reduce((s, d) => s + (fn(d) || 0), 0);

// Turns the net-worth components into the sorted, coloured donut slices.
// `publicAssetAllocation` must already be FD-free and re-based (sums to 100).
export function buildAllocation({
  publicValue, publicAssetAllocation, pmAUM, cashAltAUM, othersAUM, total,
}) {
  const allocation = [];

  if (publicValue > 0) {
    // Each class is routed to a bucket via PUBLIC_BUCKETS; unlisted classes
    // fall through to the 'Others' catch-all so none go missing.
    const bucketPct = {};
    Object.entries(publicAssetAllocation || {}).forEach(([cls, pct]) => {
      if (!(pct > 0)) return;
      const bucket = PUBLIC_BUCKETS.find(b => b.classes.includes(cls));
      const key = bucket ? bucket.category : 'public_markets_other';
      bucketPct[key] = (bucketPct[key] || 0) + pct;
    });

    [...PUBLIC_BUCKETS, PUBLIC_OTHERS_BUCKET].forEach(b => {
      const pct = bucketPct[b.category] || 0;
      // 0.5% floor only applies to the catch-all, to hide rounding dust
      if (pct <= (b.category === 'public_markets_other' ? 0.5 : 0)) return;
      const value = publicValue * pct / 100;
      allocation.push({ name: b.name, value, percentage: value / total * 100, category: b.category });
    });
  }

  const add = (name, value, category) => {
    if (value > 0) allocation.push({ name, value, percentage: (value / total) * 100, category });
  };
  add('Private Markets',      pmAUM,            'private_equity');
  add('Cash & Alternatives',  cashAltAUM,       'cash_alternatives');
  add('Others',               othersAUM,        'others');

  allocation.sort((a, b) => b.value - a.value);
  allocation.forEach((item, i) => { item.color = DONUT_COLORS[i % DONUT_COLORS.length]; });
  return allocation;
}

// Full computation from raw API data, for pages that only need the donut.
//   rawPublic  : /analytics/consolidation        fdHoldings : /markets/holdings?asset_class=fd
//   pmObjRaw   : { vc: [...], pe: [...], ... }   altObj     : { cash, alternatives, gold, art, ip }
//   reValue    : /real-estate/portfolio total_value
export function computeNetWorthAllocation({ rawPublic, fdHoldings, pmObjRaw, altObj, reValue }) {
  const fdValue = sum(fdHoldings, d => d.current_value || d.avg_price);
  const publicValue = ((rawPublic || {}).total_value || 0) - fdValue;

  const pmAUM = sum(Object.values(pmObjRaw || {}).flat(), d => d.current_value || d.nav);

  const cashAltAUM = fdValue + sum(altObj?.cash, d => d.balance) + sum(altObj?.alternatives, d => d.nav);
  const othersAUM = (reValue || 0)
    + sum(altObj?.gold, d => (d.units || 0) * (d.current_price || 0))
    + sum(altObj?.art, d => d.current_value)
    + sum(altObj?.ip, d => d.current_value);

  const total = publicValue + pmAUM + cashAltAUM + othersAUM;
  const allocation = buildAllocation({
    publicValue,
    publicAssetAllocation: rebaseAllocationExcludingFD((rawPublic || {}).asset_allocation),
    pmAUM, cashAltAUM, othersAUM, total,
  });
  return { allocation, total, publicValue, pmAUM, cashAltAUM, othersAUM };
}
